if (!customElements.get('olic-sticky-add-to-cart')) {
  customElements.define(
    'olic-sticky-add-to-cart',
    class OlicStickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        this.form = this.productInfo.querySelector('product-form form[data-type="add-to-cart-form"]');
        this.submitButton = this.form ? this.form.querySelector('.product-form__submit') : null;

        // Sin form/submit real (producto sin variante disponible para comprar): la barra queda inerte.
        if (!this.form || !this.submitButton) return;

        this.quantityInput = this.productInfo.querySelector('.product-form__quantity .quantity__input');
        this.quantityWrapper = this.quantityInput ? this.quantityInput.closest('quantity-input') : null;
        this.originalQtyMinus = this.quantityWrapper
          ? this.quantityWrapper.querySelector(".quantity__button[name='minus']")
          : null;
        this.originalQtyPlus = this.quantityWrapper
          ? this.quantityWrapper.querySelector(".quantity__button[name='plus']")
          : null;
        this.originalQuantityLabel = this.productInfo.querySelector('.quantity__label');

        this.els = {
          quantitySlot: this.querySelector('[data-olic-quantity-slot]'),
          qtyInput: this.querySelector('[data-olic-qty-input]'),
          qtyMinus: this.querySelector('[data-olic-qty-minus]'),
          qtyPlus: this.querySelector('[data-olic-qty-plus]'),
          cartQty: this.querySelector('[data-olic-cart-qty]'),
          submit: this.querySelector('[data-olic-submit]'),
          submitText: this.querySelector('[data-olic-submit-text]'),
          spinner: this.querySelector('.olic-sticky-add-to-cart__spinner'),
        };

        this.hasQuantityControl = Boolean(this.quantityInput && this.originalQtyMinus && this.originalQtyPlus);
        this.classList.toggle('olic-sticky-add-to-cart--no-quantity', !this.hasQuantityControl);
        if (this.hasQuantityControl) this.els.quantitySlot.hidden = false;

        this.onQuantityChange = this.syncQuantity.bind(this);
        this.onSubmitButtonMutate = this.syncSubmitState.bind(this);
        this.onCartQuantityMutate = this.syncCartQuantity.bind(this);
        this.onSubmitClick = this.handleSubmitClick.bind(this);
        this.onMediaChange = this.handleMediaChange.bind(this);

        this.bindEvents();
        this.syncQuantity();
        this.syncSubmitState();
        this.syncCartQuantity();
        this.initVisibilityTracking();
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.lowerBoundaryObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.cartQuantityObserver?.disconnect();
        this.quantityUpdateUnsubscriber?.();
        this.mediaQueryList?.removeEventListener('change', this.onMediaChange);
      }

      bindEvents() {
        if (this.hasQuantityControl) {
          this.els.qtyMinus.addEventListener('click', () => this.originalQtyMinus.click());
          this.els.qtyPlus.addEventListener('click', () => this.originalQtyPlus.click());
          this.quantityInput.addEventListener('change', this.onQuantityChange);
          // Cubre el reset de límites min/max al cambiar de variante, que no dispara 'change'.
          this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantityChange);
        }

        if (this.originalQuantityLabel) {
          // El label original se reescribe por completo (innerHTML) tras un add-to-cart;
          // no hay evento pub/sub que se dispare de forma fiable después de esa reescritura,
          // así que se observa el nodo estable (el <label> en sí no se reemplaza).
          this.cartQuantityObserver = new MutationObserver(this.onCartQuantityMutate);
          this.cartQuantityObserver.observe(this.originalQuantityLabel, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        this.els.submit.addEventListener('click', this.onSubmitClick);

        // Único MutationObserver, acotado al botón original: cubre variante, agotado,
        // combinación no disponible y el estado de loading durante el fetch de /cart/add.
        this.mutationObserver = new MutationObserver(this.onSubmitButtonMutate);
        this.mutationObserver.observe(this.submitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          characterData: true,
          subtree: true,
        });
      }

      handleSubmitClick() {
        if (this.els.submit.disabled) return;
        this.form.requestSubmit(this.submitButton);
      }

      syncQuantity() {
        if (!this.hasQuantityControl) return;
        this.els.qtyInput.value = this.quantityInput.value;
        this.els.qtyMinus.classList.toggle('disabled', this.originalQtyMinus.classList.contains('disabled'));
        this.els.qtyPlus.classList.toggle('disabled', this.originalQtyPlus.classList.contains('disabled'));
      }

      syncCartQuantity() {
        if (!this.els.cartQty) return;

        const sourceWrapper = this.originalQuantityLabel?.querySelector('.quantity__rules-cart');
        const sourceText = sourceWrapper?.querySelector('span')?.textContent.trim();
        const isVisible = Boolean(sourceWrapper && !sourceWrapper.classList.contains('hidden') && sourceText);

        this.els.cartQty.textContent = isVisible ? sourceText : '';
      }

      syncSubmitState() {
        const isDisabled = this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true';
        this.els.submit.disabled = isDisabled;
        this.els.submit.classList.toggle('loading', this.submitButton.classList.contains('loading'));

        const sourceText = this.submitButton.querySelector('span');
        if (sourceText) this.els.submitText.textContent = sourceText.textContent.trim();

        if (this.els.spinner) {
          const sourceSpinner = this.submitButton.querySelector('.loading__spinner');
          this.els.spinner.classList.toggle('hidden', !sourceSpinner || sourceSpinner.classList.contains('hidden'));
        }
      }

      initVisibilityTracking() {
        // Por defecto se asume visible/dentro de zona de recomendaciones para no
        // mostrar la barra hasta que los observers confirmen lo contrario (evita parpadeo).
        this.isOriginalButtonVisible = true;
        this.isPastRecommendationsSection = false;

        this.mediaQueryList = window.matchMedia('(max-width: 749px)');
        this.mediaQueryList.addEventListener('change', this.onMediaChange);
        if (this.mediaQueryList.matches) this.setupObservers();
      }

      handleMediaChange(event) {
        if (event.matches) {
          this.setupObservers();
        } else {
          this.intersectionObserver?.disconnect();
          this.intersectionObserver = null;
          this.lowerBoundaryObserver?.disconnect();
          this.lowerBoundaryObserver = null;
          this.classList.remove('olic-sticky-add-to-cart--visible');
        }
      }

      setupObservers() {
        this.setupPrimaryObserver();
        this.setupLowerBoundaryObserver();
      }

      setupPrimaryObserver() {
        if (this.intersectionObserver) return;
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this.isOriginalButtonVisible = entries[entries.length - 1].isIntersecting;
            this.updateVisibility();
          },
          { threshold: 0 }
        );
        this.intersectionObserver.observe(this.submitButton);
      }

      // OLIC: a partir de la sección de recomendaciones ("También te puede interesar")
      // la barra deja de mostrarse, aunque el botón original siga fuera del viewport.
      setupLowerBoundaryObserver() {
        if (this.lowerBoundaryObserver) return;

        this.recommendationsSection = document.querySelector('.related-products');
        if (!this.recommendationsSection) return;

        this.lowerBoundaryObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            this.isPastRecommendationsSection = entry.isIntersecting || entry.boundingClientRect.top < 0;
            this.updateVisibility();
          },
          { threshold: 0 }
        );
        this.lowerBoundaryObserver.observe(this.recommendationsSection);
      }

      updateVisibility() {
        const shouldShow = !this.isOriginalButtonVisible && !this.isPastRecommendationsSection;
        this.classList.toggle('olic-sticky-add-to-cart--visible', shouldShow);
      }
    }
  );
}
